import React, { createContext, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { selectIsAuthenticatedFromState } from 'src/redux/selectors/auth.selectors'

export type SessionStatus = {
  /**
   * Raw authenticated-session flag; not gated on the user having loaded. For
   * "authenticated AND user loaded", use UserProvider's `useAuthedUser`.
   */
  isAuthenticated: boolean
}

export const SessionStatusContext = createContext<SessionStatus | undefined>(
  undefined,
)

/**
 * Reactive context for the raw authenticated-session flag, so components subscribe
 * to it instead of reading Redux directly. Distinct from UserProvider's
 * user-resolved `authenticated` (which also requires the user to have loaded); do
 * not fold them together. The read must stay reactive (`useSelector`); a plain
 * getter would freeze render gates that depend on this value.
 */
export const SessionStatusProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const isAuthenticated = useSelector(selectIsAuthenticatedFromState)
  const value = useMemo<SessionStatus>(
    () => ({ isAuthenticated }),
    [isAuthenticated],
  )
  return (
    <SessionStatusContext.Provider value={value}>
      {children}
    </SessionStatusContext.Provider>
  )
}
